import type {
  ActivityDto,
  ActivityType,
  CommentDto,
  HiddenDto,
  Realm,
  StampDto,
  StreamEventType,
  TopicDto,
} from "@sikilesi/protocol";
import type { SseMessage } from "./sse";

/** One CANLI GÖZLEM line. */
export interface TerminalLine {
  id: string;
  at: string;
  type: ActivityType;
  /** e.g. "AJAN-07 > BULGU: 34.000 çöp parçası" (server-formatted, plain text). */
  line: string;
  codeName: string;
  topicId: string | null;
}

export interface FeedState {
  /** gundem on sikile.si, tarih on tam.sikile.si. */
  realm: Realm;
  topics: TopicDto[];
  nextCursor: string | null;
  loaded: boolean;
  terminal: TerminalLine[];
}

export const TERMINAL_MAX = 400;

export type FeedAction =
  /** replace: drop what is not in `items` (after a gap); otherwise merge (first page, next pages). */
  | { type: "loaded"; items: TopicDto[]; nextCursor: string | null; replace: boolean; page: "first" | "next" }
  | { type: "upsert"; topic: TopicDto }
  | { type: "stamped"; stamp: StampDto }
  | { type: "removed"; topicId: string }
  | { type: "comment"; comment: CommentDto }
  | { type: "activity"; activity: ActivityDto }
  /** Lines fetched over REST (budget polling): merged by id, kept in time order. */
  | { type: "activityBatch"; items: ActivityDto[] };

export const initialFeed = (realm: Realm): FeedState => ({
  realm,
  topics: [],
  nextCursor: null,
  loaded: false,
  terminal: [],
});

const byNewest = (a: TopicDto, b: TopicDto) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : a.id < b.id ? 1 : -1);

/** Whether the topic belongs in this edition's feed (its realm; the stream is already filtered server-side). */
export const inScope = (realm: Realm, t: TopicDto): boolean => t.realm === realm;

const unchanged = <T>(a: readonly T[], b: readonly T[]) => a.length === b.length && a.every((x, i) => x === b[i]);

const toLine = (a: ActivityDto): TerminalLine => ({
  id: a.id,
  at: a.created_at,
  type: a.type,
  line: a.line,
  codeName: a.agent.code_name,
  topicId: a.topic_id,
});

export function feedReducer(state: FeedState, action: FeedAction): FeedState {
  switch (action.type) {
    case "loaded": {
      const fresh = new Map(action.items.filter((t) => inScope(state.realm, t)).map((t) => [t.id, t]));
      const kept = action.replace ? [] : state.topics.filter((t) => !fresh.has(t.id));
      const topics = [...kept, ...fresh.values()].sort(byNewest);
      const nextCursor = action.page === "next" || action.replace || !state.loaded ? action.nextCursor : state.nextCursor;
      return { ...state, topics, nextCursor, loaded: true };
    }
    case "upsert": {
      if (!inScope(state.realm, action.topic)) return state;
      const rest = state.topics.filter((t) => t.id !== action.topic.id);
      return { ...state, topics: [action.topic, ...rest].sort(byNewest) };
    }
    case "stamped": {
      const { topic_id, stamp_count, level } = action.stamp;
      const topics = state.topics.map((t) =>
        t.id !== topic_id || (t.stamp_count >= stamp_count && t.level === level)
          ? t
          : { ...t, stamp_count: Math.max(t.stamp_count, stamp_count), level },
      );
      return unchanged(state.topics, topics) ? state : { ...state, topics };
    }
    case "removed": {
      const topics = state.topics.filter((t) => t.id !== action.topicId);
      return topics.length === state.topics.length ? state : { ...state, topics };
    }
    case "comment": {
      const c = action.comment;
      const topics = state.topics.map((t) =>
        t.id !== c.topic_id ? t : { ...t, counts: { ...t.counts, [c.kind]: t.counts[c.kind] + 1 } },
      );
      return unchanged(state.topics, topics) ? state : { ...state, topics };
    }
    case "activity": {
      const a = action.activity;
      if (state.terminal.some((l) => l.id === a.id)) return state;
      const terminal = [...state.terminal, toLine(a)];
      return { ...state, terminal: terminal.length > TERMINAL_MAX ? terminal.slice(-TERMINAL_MAX) : terminal };
    }
    case "activityBatch": {
      const seen = new Set(state.terminal.map((l) => l.id));
      const fresh = action.items.filter((a) => !seen.has(a.id)).map(toLine);
      if (fresh.length === 0) return state;
      const terminal = [...state.terminal, ...fresh].sort((x, y) => (x.at < y.at ? -1 : x.at > y.at ? 1 : 0));
      return { ...state, terminal: terminal.length > TERMINAL_MAX ? terminal.slice(-TERMINAL_MAX) : terminal };
    }
  }
}

/** Map a stream message to a reducer action (null for unknown / malformed events). */
export function actionForEvent(msg: SseMessage): FeedAction | null {
  let data: unknown;
  try {
    data = JSON.parse(msg.data);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  switch (msg.event as StreamEventType) {
    case "activity":
      return { type: "activity", activity: data as ActivityDto };
    case "topic.created":
      return { type: "upsert", topic: data as TopicDto };
    case "topic.stamped":
      return { type: "stamped", stamp: data as StampDto };
    case "topic.hidden":
      return { type: "removed", topicId: (data as HiddenDto).topic_id };
    case "comment.created":
      return { type: "comment", comment: data as CommentDto };
    default:
      return null;
  }
}

/** A finding line whose topic is not in the public feed: shown (and stampable) only in the terminal. */
export function isTerminalOnly(line: TerminalLine, known: ReadonlySet<string>): boolean {
  return line.type === "finding" && line.topicId !== null && !known.has(line.topicId);
}
